import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import TopHeader from '../components/TopHeader';
import ProcessStages from '../components/ProcessStages';
import FileUpload from '../components/FileUpload';
import CaseTimeline from '../components/CaseTimeline';
import AgreementSigning from '../components/AgreementSigning';
import CaseProgressBar from '../components/CaseProgressBar';
import DocumentViewer from '../components/DocumentViewer';
import { caseAPI, clientAPI, agreementAPI, documentAPI, activityAPI, caseServiceAPI, checklistAPI, visaProgramAPI, familyAPI, completeFileAPI, fileUtils } from '../utils/api';
import { FileText, Upload, Clock, CheckCircle, AlertTriangle, LayoutDashboard, FolderOpen, XCircle, Info, Eye, Download, FileSignature, Users, ChevronDown, ChevronUp, AlertCircle, Shield, Star, X, Loader2, ShieldAlert } from 'lucide-react';
import EscalationModal from '../components/EscalationModal';
import { toast } from 'sonner';

export default function ClientPortal() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine active tab from URL
  const getActiveTab = () => {
    if (location.pathname.includes('/documents')) return 'documents';
    if (location.pathname.includes('/application')) return 'application';
    return 'dashboard';
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTab());
  const [myCase, setMyCase] = useState(null);
  const [client, setClient] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [processStages, setProcessStages] = useState([]);
  const [currentStage, setCurrentStage] = useState('');
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedChecklistItem, setSelectedChecklistItem] = useState(null);
  const [visaProgram, setVisaProgram] = useState(null);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [documentSummary, setDocumentSummary] = useState(null);
  const [uploadContext, setUploadContext] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});
  // Client review of officer-prepared documents
  const [reviewingOfficerDoc, setReviewingOfficerDoc] = useState(null); // slot object
  const [officerDocNotes, setOfficerDocNotes] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  // Escalation
  const [showEscalation, setShowEscalation] = useState(false);
  // Complete File
  const [completeFile, setCompleteFile] = useState(null);
  const [showCompleteFile, setShowCompleteFile] = useState(false);
  const [cfChecks, setCfChecks] = useState({ c1: false, c2: false, c3: false });
  const [cfFeedback, setCfFeedback] = useState('');
  const [submittingCf, setSubmittingCf] = useState(false);

  // Sync tab with URL
  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [location.pathname]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const casesRes = await caseAPI.getCases();
      if (casesRes.data.length > 0) {
        const caseData = casesRes.data[0];
        setMyCase(caseData);
        
        const [clientRes, docsRes, timelineRes] = await Promise.all([
          clientAPI.getClient(caseData.client_id),
          documentAPI.getCaseDocuments(caseData.id),
          activityAPI.getCaseTimeline(caseData.id)
        ]);
        
        setClient(clientRes.data);
        setDocuments(docsRes.data);
        setTimeline(timelineRes.data);

        // Fetch process stages
        try {
          const stagesRes = await caseServiceAPI.getProcessStages(caseData.id);
          setProcessStages(stagesRes.data.stages || []);
          setCurrentStage(stagesRes.data.current_stage || '');
        } catch (err) {
          console.log('Could not fetch process stages');
        }

        // Fetch agreement
        try {
          const agreementRes = await agreementAPI.getByCase(caseData.id);
          if (agreementRes.data && agreementRes.data.length > 0) {
            setAgreement(agreementRes.data[0]);
          }
        } catch (err) {
          console.log('No agreement yet');
        }
        
        // Fetch visa program info
        try {
          const programRes = await visaProgramAPI.getProgram(caseData.visa_program_id);
          setVisaProgram(programRes.data);
        } catch (err) {
          console.log('Could not fetch visa program');
        }
        
        // Fetch time remaining
        try {
          const timeRes = await caseServiceAPI.getTimeRemaining(caseData.id);
          setTimeRemaining(timeRes.data);
        } catch (err) {
          console.log('Could not fetch time remaining');
        }

        // Fetch checklist for the visa program
        try {
          const checklistRes = await checklistAPI.getChecklistByProgram(caseData.visa_program_id);
          setChecklist(checklistRes.data.items || []);
        } catch (err) {
          setChecklist([
            { name: 'Passport Copy', required: true },
            { name: 'Bank Statements', required: true },
            { name: 'Employment Letter', required: true },
            { name: 'Salary Slips', required: true },
            { name: 'Educational Documents', required: false },
            { name: 'Travel Insurance', required: true },
          ]);
        }

        // Fetch family-aware document summary
        try {
          const summaryRes = await familyAPI.getDocumentSummary(caseData.id);
          setDocumentSummary(summaryRes.data);
        } catch (err) {
          console.log('Could not fetch document summary');
        }

        // Fetch complete file
        try {
          const cfRes = await completeFileAPI.get(caseData.id);
          setCompleteFile(cfRes.data);
        } catch (err) {
          setCompleteFile(null);
        }
      }
    } catch (error) {
      toast.error('Failed to load case data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const routes = {
      dashboard: '/client',
      application: '/client/application',
      documents: '/client/documents'
    };
    navigate(routes[tab]);
  };

  const handleSignAgreement = async () => {
    if (!agreement) return;
    try {
      await agreementAPI.signAgreement(agreement.id, { signature: 'Digital Signature' });
      toast.success('Agreement signed successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to sign agreement');
    }
  };

  const handleOpenUploadModal = (ctx) => {
    // ctx can be a string (legacy) or an object with full family context
    if (typeof ctx === 'string') {
      setUploadContext({ checklistItemName: ctx, applicantType: 'primary', applicantId: null, applicantName: null, documentSideKey: null, documentSideLabel: null, customRequestId: null });
      setSelectedChecklistItem(ctx);
    } else {
      setUploadContext(ctx);
      setSelectedChecklistItem(ctx.checklistItemName);
    }
    setShowUploadModal(true);
  };

  const handleUploadSuccess = (data) => {
    setShowUploadModal(false);
    setSelectedChecklistItem(null);
    setUploadContext(null);
    fetchData();
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setSelectedChecklistItem(null);
    setUploadContext(null);
  };

  const toggleSection = (sectionKey) => {
    setCollapsedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  // ── Officer doc review ────────────────────────────────────────────────────
  const handleOfficerDocReview = async (action) => {
    if (action === 'rejected' && officerDocNotes.trim().length < 5) {
      toast.error('Please describe what needs to be changed');
      return;
    }
    setSubmittingReview(true);
    try {
      await documentAPI.clientReview(reviewingOfficerDoc.document_id, {
        action,
        client_notes: officerDocNotes.trim() || null,
      });
      toast.success(action === 'approved' ? 'Document approved' : 'Change request sent to your officer');
      setReviewingOfficerDoc(null);
      setOfficerDocNotes('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  // ── Complete File ─────────────────────────────────────────────────────────
  const handleCfApprove = async () => {
    if (!cfChecks.c1 || !cfChecks.c2 || !cfChecks.c3) {
      toast.error('Please confirm all three acknowledgements before approving');
      return;
    }
    setSubmittingCf(true);
    try {
      await completeFileAPI.approve(myCase.id, {
        acknowledgement_1: 'I have reviewed all documents in my application file and confirm they are complete and accurate.',
        acknowledgement_2: 'I confirm that all information provided is true and accurate to the best of my knowledge.',
        acknowledgement_3: 'I authorise ImmiWays Migration Advisory to submit my visa application on my behalf.',
      });
      toast.success('Complete File approved. Your officer will now proceed with filing.');
      setCfChecks({ c1: false, c2: false, c3: false });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit approval');
    } finally {
      setSubmittingCf(false);
    }
  };

  const handleCfReject = async () => {
    if (cfFeedback.trim().length < 20) {
      toast.error('Please provide detailed feedback (at least 20 characters)');
      return;
    }
    setSubmittingCf(true);
    try {
      await completeFileAPI.reject(myCase.id, { client_feedback: cfFeedback.trim() });
      toast.success('Your feedback has been sent to your case officer');
      setCfFeedback('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmittingCf(false);
    }
  };

  const getSlotStatus = (slot) => {
    if (slot.status === 'approved') return 'approved';
    if (slot.status === 'rejected') return 'rejected';
    if (slot.status === 'uploaded') return 'pending';
    return 'not_uploaded';
  };

  const getSlotDoc = (applicantId, itemName, sideKey) => {
    return documents.find(d =>
      d.checklist_item_name === itemName &&
      (d.applicant_id || null) === (applicantId || null) &&
      (d.document_side_key || null) === (sideKey || null)
    );
  };

  const getProgressPercentage = () => {
    // Use backend-calculated progress if available
    if (myCase?.progress_percentage !== undefined) {
      return myCase.progress_percentage;
    }
    // Fallback to stage-based calculation
    if (!processStages.length) return 0;
    const completed = processStages.filter(s => s.is_completed).length;
    return Math.round((completed / processStages.length) * 100);
  };

  const getDocDeadlineInfo = () => {
    if (!timeRemaining?.doc_deadline_days_remaining && timeRemaining?.doc_deadline_days_remaining !== 0) return null;
    const days = timeRemaining.doc_deadline_days_remaining;
    if (days <= 0) return { text: 'Document deadline passed!', urgent: true };
    if (days <= 5) return { text: `${days} days left to submit documents`, urgent: true };
    return { text: `${days} days remaining for document submission`, urgent: false };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (!myCase) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <div className="main-content bg-slate-50" data-testid="client-portal">
          {/* Header with ProfileMenu */}
          <TopHeader 
            title="Welcome" 
            subtitle="Your visa application portal"
          />
          <div className="text-center py-16">
            <FileText size={64} className="mx-auto text-slate-300 mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">No Active Application</h2>
            <p className="text-slate-600">You don't have any active visa applications yet.</p>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'application', label: 'My Application', icon: FileText },
    { id: 'documents', label: 'Documents', icon: FolderOpen },
    { id: 'timeline', label: 'Timeline', icon: Clock },
  ];

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content bg-slate-50" data-testid="client-portal">
        {/* Header with ProfileMenu */}
        <TopHeader 
          title={`Welcome, ${client?.full_name}`}
          subtitle={`${client?.visa_type} - ${client?.country_applying_to}`}
        />

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 border-b border-slate-200 pb-4" data-testid="portal-tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-teal-500 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
          {/* Escalate button — always visible, right-aligned */}
          <div className="ml-auto">
            <button
              onClick={() => setShowEscalation(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all text-sm"
            >
              <ShieldAlert size={16} />
              Raise Escalation
            </button>
          </div>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6" data-testid="dashboard-content">
            {/* Case Progress Bar */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <CaseProgressBar progress={getProgressPercentage()} showMilestones={true} />
            </div>

            {/* Process Stages */}
            <ProcessStages stages={processStages} currentStage={currentStage} />

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <Clock className="text-teal-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Progress</p>
                    <p className="text-2xl font-bold text-slate-900">{getProgressPercentage()}%</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FolderOpen className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Documents</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {documents.filter(d => d.status === 'approved').length}/{checklist.length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    timeRemaining?.is_expired ? 'bg-red-100' : 'bg-green-100'
                  }`}>
                    <AlertTriangle className={timeRemaining?.is_expired ? 'text-red-600' : 'text-green-600'} size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Days Remaining</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {timeRemaining?.is_expired ? 'Expired' : timeRemaining?.days_remaining || '--'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Period Alert */}
            {timeRemaining && (
              <div className={`p-4 rounded-lg border ${
                timeRemaining.is_expired 
                  ? 'bg-red-50 border-red-200' 
                  : timeRemaining.days_remaining < 7 
                    ? 'bg-amber-50 border-amber-200' 
                    : 'bg-blue-50 border-blue-200'
              }`} data-testid="service-period-alert">
                <div className="flex items-center gap-3">
                  {timeRemaining.is_expired ? (
                    <AlertTriangle className="text-red-600" size={24} />
                  ) : (
                    <Clock className="text-blue-600" size={24} />
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">
                      {timeRemaining.is_expired ? 'Service Period Expired' : `Service Valid Until ${new Date(timeRemaining.service_end_date).toLocaleDateString()}`}
                    </p>
                    {timeRemaining.is_expired && (
                      <p className="text-sm text-red-600 mt-1">
                        Please contact support to extend your service period.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Recent Activity */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Recent Activity</h3>
              <div className="space-y-3">
                {timeline.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No recent activities</p>
                ) : (
                  timeline.slice(0, 5).map((activity) => (
                    <div key={activity.id} className="flex gap-3 items-start">
                      <div className="w-2 h-2 rounded-full bg-teal-500 mt-2"></div>
                      <div>
                        <p className="text-sm text-slate-900">{activity.description}</p>
                        <p className="text-xs text-slate-500">{new Date(activity.timestamp).toLocaleString()}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Application Tab */}
        {activeTab === 'application' && (
          <div className="space-y-6" data-testid="application-content">
            {/* Current Stage Card */}
            <div className="bg-gradient-to-r from-teal-500 to-emerald-500 rounded-xl p-6 text-white">
              <h2 className="text-2xl font-bold mb-2">Current Stage</h2>
              <p className="text-lg opacity-90">{currentStage || 'Loading...'}</p>
              <div className="mt-4">
                <div className="w-full bg-white/20 rounded-full h-2">
                  <div 
                    className="bg-white h-2 rounded-full transition-all duration-500" 
                    style={{ width: `${getProgressPercentage()}%` }}
                  ></div>
                </div>
                <p className="text-sm mt-2 opacity-90">{getProgressPercentage()}% Complete</p>
              </div>
            </div>

            {/* Process Stages */}
            <ProcessStages stages={processStages} currentStage={currentStage} />

            {/* Agreement Signing Component */}
            {myCase && visaProgram && (
              <AgreementSigning 
                caseId={myCase.id}
                clientName={client?.full_name || 'Client'}
                visaType={visaProgram?.visa_type || 'Visa'}
                country={visaProgram?.country || 'Destination'}
                onSigned={() => {
                  fetchData();
                  toast.success('Agreement signed! Document checklist is now unlocked.');
                }}
              />
            )}

            {/* Timeline */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Application Timeline</h3>
              <div className="space-y-4">
                {timeline.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No activities yet</p>
                ) : (
                  timeline.map((activity) => (
                    <div key={activity.id} className="flex gap-4" data-testid={`timeline-item-${activity.id}`}>
                      <div className="flex flex-col items-center">
                        <div className="w-3 h-3 rounded-full bg-teal-500"></div>
                        <div className="w-0.5 h-full bg-slate-200 mt-2"></div>
                      </div>
                      <div className="pb-4">
                        <p className="font-medium text-slate-900">{activity.description}</p>
                        <p className="text-sm text-slate-500">{new Date(activity.timestamp).toLocaleString()}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="space-y-6" data-testid="documents-content">

            {/* Complete File Action Banner */}
            {completeFile?.status === 'pending_client' && (
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-5 text-white shadow-lg">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
                      <Shield size={24} className="text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-lg">Action Required — Review Your Complete File</p>
                      <p className="text-indigo-200 text-sm mt-0.5">Your case officer has prepared your complete application file. Please review all documents and approve to authorise filing.</p>
                    </div>
                  </div>
                  <button onClick={() => setShowCompleteFile(true)} className="shrink-0 px-5 py-2.5 bg-white text-indigo-700 rounded-xl font-bold text-sm hover:bg-indigo-50 transition-all whitespace-nowrap">
                    Review &amp; Approve →
                  </button>
                </div>
              </div>
            )}

            {completeFile?.status === 'approved' && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-4">
                <CheckCircle size={28} className="text-green-500 shrink-0" />
                <div className="flex-1">
                  <p className="font-bold text-green-800">Complete File Approved</p>
                  <p className="text-sm text-green-600">You approved your complete file on {new Date(completeFile.approval?.approved_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}. Your officer is now proceeding with filing.</p>
                </div>
                <button onClick={() => setShowCompleteFile(true)} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">View Record</button>
              </div>
            )}

            {completeFile?.status === 'changes_requested' && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-4">
                <AlertTriangle size={24} className="text-amber-500 shrink-0" />
                <div>
                  <p className="font-bold text-amber-800">Changes Requested — Officer is Revising</p>
                  <p className="text-sm text-amber-600">Your feedback has been sent. Your officer will update the file and send it back for your review.</p>
                </div>
              </div>
            )}

            {/* Document Deadline Alert */}
            {getDocDeadlineInfo() && (
              <div className={`p-4 rounded-lg border flex items-center gap-3 ${getDocDeadlineInfo().urgent ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`} data-testid="doc-deadline-alert">
                <Clock className={getDocDeadlineInfo().urgent ? 'text-red-600' : 'text-blue-600'} size={24} />
                <p className={`font-medium ${getDocDeadlineInfo().urgent ? 'text-red-700' : 'text-blue-700'}`}>{getDocDeadlineInfo().text}</p>
              </div>
            )}

            {/* Legend */}
            <div className="flex flex-wrap gap-4 text-sm px-1">
              <div className="flex items-center gap-1.5"><CheckCircle size={15} className="text-green-500" /><span className="text-slate-600">Approved</span></div>
              <div className="flex items-center gap-1.5"><Clock size={15} className="text-amber-500" /><span className="text-slate-600">Under Review</span></div>
              <div className="flex items-center gap-1.5"><XCircle size={15} className="text-red-500" /><span className="text-slate-600">Rejected</span></div>
              <div className="flex items-center gap-1.5"><Info size={15} className="text-slate-400" /><span className="text-slate-600">Not yet uploaded</span></div>
              <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-indigo-400 inline-block" /><span className="text-slate-600">Awaiting officer</span></div>
              <div className="flex items-center gap-1.5"><Star size={15} className="text-purple-500" /><span className="text-slate-600">Ready for your review</span></div>
            </div>

            {/* ── FAMILY-GROUPED DOCUMENT SECTIONS ── */}
            {documentSummary ? (
              <>
                {/* Build sections: primary first, then each family member */}
                {[
                  { ...documentSummary.primary, sectionKey: 'primary', label: `Primary Applicant (You — ${documentSummary.primary.applicant_name})`, icon: '👤', headerColor: 'bg-teal-50 border-teal-200' },
                  ...documentSummary.family_members.map(m => ({
                    ...m,
                    sectionKey: m.applicant_id,
                    label: `${m.applicant_type === 'spouse' ? 'Spouse' : 'Child'} — ${m.applicant_name} (${m.relation})`,
                    icon: m.applicant_type === 'spouse' ? '💑' : '👦',
                    headerColor: m.applicant_type === 'spouse' ? 'bg-pink-50 border-pink-200' : 'bg-yellow-50 border-yellow-200'
                  }))
                ].map(section => {
                  const { approved, total, rejected } = section.stats;
                  const pct = total > 0 ? Math.round((approved / total) * 100) : 0;
                  const isCollapsed = collapsedSections[section.sectionKey];

                  // Group slots by item_name to render multi-side as one card
                  const grouped = section.slots.reduce((acc, slot) => {
                    if (!acc[slot.item_name]) acc[slot.item_name] = [];
                    acc[slot.item_name].push(slot);
                    return acc;
                  }, {});

                  return (
                    <div key={section.sectionKey} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                      {/* Section header */}
                      <button
                        className={`w-full flex items-center justify-between p-5 border-b ${section.headerColor} transition-all`}
                        onClick={() => toggleSection(section.sectionKey)}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{section.icon}</span>
                          <div className="text-left">
                            <p className="font-bold text-slate-900">{section.label}</p>
                            <p className="text-sm text-slate-500 mt-0.5">{approved} of {total} documents approved</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {/* Mini progress bar */}
                          <div className="hidden sm:flex items-center gap-2">
                            <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div className="h-2 bg-teal-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            <span className={`text-sm font-bold ${pct === 100 ? 'text-green-600' : 'text-slate-700'}`}>{pct}%</span>
                          </div>
                          {rejected > 0 && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">{rejected} rejected</span>
                          )}
                          {isCollapsed ? <ChevronDown size={20} className="text-slate-400" /> : <ChevronUp size={20} className="text-slate-400" />}
                        </div>
                      </button>

                      {/* Document slots */}
                      {!isCollapsed && (
                        <div className="p-5 space-y-3">
                          {Object.entries(grouped).map(([itemName, slots]) => {
                            const isOfficerDoc = slots[0].upload_by === 'officer';

                            // Multi-side item
                            if (slots.length > 1 || slots[0].side_key) {
                              return (
                                <div key={itemName} className="border border-slate-200 rounded-xl overflow-hidden">
                                  <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                                    <p className="text-sm font-semibold text-slate-700">{itemName}</p>
                                    {isOfficerDoc && <span className="text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full font-medium">Prepared by officer</span>}
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-200">
                                    {slots.map(slot => {
                                      const status = getSlotStatus(slot);
                                      const isOfficerPending = isOfficerDoc && status === 'not_uploaded';
                                      const isReadyForClientReview = isOfficerDoc && status === 'pending';
                                      const bgCls = status === 'approved' ? 'bg-green-50' : status === 'rejected' ? 'bg-red-50' : isOfficerPending ? 'bg-indigo-50' : isReadyForClientReview ? 'bg-purple-50' : status === 'pending' ? 'bg-amber-50' : 'bg-white';
                                      return (
                                        <div key={slot.side_key || 'single'} className={`p-4 ${bgCls}`}>
                                          <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-slate-700">{slot.side_label || slot.side_key}</span>
                                            {isOfficerPending ? <span className="text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">Awaiting officer</span>
                                              : isReadyForClientReview ? <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">Review required</span>
                                              : <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${status === 'approved' ? 'bg-green-100 text-green-700' : status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{status === 'approved' ? 'Approved' : status === 'rejected' ? 'Changes Requested' : 'Under Review'}</span>}
                                          </div>
                                          <div className="flex gap-2 flex-wrap">
                                            {!isOfficerDoc && (status === 'not_uploaded' || status === 'rejected') && (
                                              <button onClick={() => handleOpenUploadModal({ checklistItemName: itemName, applicantType: section.applicant_type, applicantId: section.applicant_id, applicantName: section.applicant_type !== 'primary' ? section.applicant_name : null, documentSideKey: slot.side_key, documentSideLabel: slot.side_label, upload_by: 'client' })}
                                                className="flex items-center gap-1 px-3 py-1.5 bg-teal-500 text-white rounded-lg text-xs font-medium hover:bg-teal-600">
                                                <Upload size={11} />{status === 'rejected' ? 'Re-upload' : 'Upload'}
                                              </button>
                                            )}
                                            {isReadyForClientReview && (
                                              <button onClick={() => { setReviewingOfficerDoc(slot); setOfficerDocNotes(''); }}
                                                className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700">
                                                <Star size={11} /> Review Now
                                              </button>
                                            )}
                                            {slot.file_url && (
                                              <>
                                                <button onClick={() => setViewingDocument({ file_url: slot.file_url, file_name: slot.file_name })} className="p-1.5 border border-slate-200 text-slate-500 rounded-lg hover:text-teal-600"><Eye size={13} /></button>
                                                <button onClick={() => fileUtils.downloadFile(slot.file_url, slot.file_name)} className="p-1.5 border border-slate-200 text-slate-500 rounded-lg hover:text-teal-600"><Download size={13} /></button>
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            }

                            // Single-slot item
                            const slot = slots[0];
                            const status = getSlotStatus(slot);
                            const isOfficerPending = isOfficerDoc && status === 'not_uploaded';
                            const isReadyForClientReview = isOfficerDoc && status === 'pending';
                            const borderCls = status === 'approved' ? 'bg-green-50 border-green-200' : status === 'rejected' ? 'bg-red-50 border-red-200' : isOfficerPending ? 'bg-indigo-50 border-indigo-200' : isReadyForClientReview ? 'bg-purple-50 border-purple-200' : status === 'pending' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200';

                            return (
                              <div key={itemName} className={`p-4 rounded-xl border transition-all ${borderCls}`}>
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex items-start gap-3 flex-1 min-w-0">
                                    {status === 'approved' ? <CheckCircle size={20} className="text-green-500 mt-0.5 shrink-0" />
                                      : status === 'rejected' ? <XCircle size={20} className="text-red-500 mt-0.5 shrink-0" />
                                      : isOfficerPending ? <Clock size={20} className="text-indigo-400 mt-0.5 shrink-0" />
                                      : isReadyForClientReview ? <Star size={20} className="text-purple-500 mt-0.5 shrink-0" />
                                      : status === 'pending' ? <Clock size={20} className="text-amber-500 mt-0.5 shrink-0" />
                                      : <Info size={20} className="text-slate-400 mt-0.5 shrink-0" />}
                                    <div className="min-w-0">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <p className="font-semibold text-slate-900 text-sm">{itemName}</p>
                                        {isOfficerDoc && <span className="text-xs bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded font-medium">Officer prepares</span>}
                                      </div>
                                      <div className="mt-1">
                                        {isOfficerPending ? <span className="text-xs text-indigo-600 font-medium">Your officer is preparing this document</span>
                                          : isReadyForClientReview ? <span className="text-xs text-purple-700 font-semibold">Ready for your review — click to approve or request changes</span>
                                          : status === 'approved' ? <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">{isOfficerDoc ? 'Approved by you' : 'Approved'}</span>
                                          : status === 'rejected' ? (
                                            <div>
                                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">{isOfficerDoc ? 'Changes Requested' : 'Rejected'}</span>
                                              {slot.reviewer_notes && <p className="text-xs text-red-600 mt-1"><strong>Reason:</strong> {slot.reviewer_notes}</p>}
                                              {slot.client_notes && <p className="text-xs text-red-600 mt-0.5"><strong>Your feedback:</strong> {slot.client_notes}</p>}
                                            </div>
                                          ) : <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Under Review</span>}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    {!isOfficerDoc && (status === 'not_uploaded' || status === 'rejected') && (
                                      <button onClick={() => handleOpenUploadModal({ checklistItemName: itemName, applicantType: section.applicant_type, applicantId: section.applicant_id, applicantName: section.applicant_type !== 'primary' ? section.applicant_name : null, documentSideKey: null, documentSideLabel: null, upload_by: 'client' })}
                                        className="flex items-center gap-1.5 px-3 py-2 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600">
                                        <Upload size={14} />{status === 'rejected' ? 'Re-upload' : 'Upload'}
                                      </button>
                                    )}
                                    {isReadyForClientReview && (
                                      <button onClick={() => { setReviewingOfficerDoc(slot); setOfficerDocNotes(''); }}
                                        className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-bold hover:bg-purple-700 transition-all">
                                        <Star size={14} /> Review Now
                                      </button>
                                    )}
                                    {slot.file_url && (
                                      <>
                                        <button onClick={() => setViewingDocument({ file_url: slot.file_url, file_name: slot.file_name })} className="p-2 border border-slate-200 text-slate-500 rounded-lg hover:text-teal-600"><Eye size={16} /></button>
                                        <button onClick={() => fileUtils.downloadFile(slot.file_url, slot.file_name)} className="p-2 border border-slate-200 text-slate-500 rounded-lg hover:text-teal-600"><Download size={16} /></button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {section.slots.length === 0 && (
                            <p className="text-sm text-slate-400 py-4 text-center">No documents required for this applicant yet.</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            ) : (
              /* Fallback: legacy flat checklist if summary API not available */
              <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="document-checklist">
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Upload size={24} className="text-teal-600" />Document Checklist
                </h3>
                <div className="space-y-3">
                  {checklist.map((item) => {
                    const uploaded = documents.find(d => d.checklist_item_name === item.name && d.applicant_type === 'primary');
                    const status = uploaded?.status === 'approved' ? 'approved' : uploaded?.status === 'rejected' ? 'rejected' : uploaded ? 'pending' : 'not_uploaded';
                    return (
                      <div key={item.name} className={`p-4 rounded-lg border ${status === 'approved' ? 'bg-green-50 border-green-200' : status === 'rejected' ? 'bg-red-50 border-red-200' : status === 'pending' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {status === 'approved' ? <CheckCircle size={20} className="text-green-500" /> : status === 'rejected' ? <XCircle size={20} className="text-red-500" /> : status === 'pending' ? <Clock size={20} className="text-amber-500" /> : <Info size={20} className="text-slate-400" />}
                            <div>
                              <p className="font-medium text-slate-900">{item.name}{item.required && <span className="text-red-500 ml-1">*</span>}</p>
                              {status === 'rejected' && uploaded?.reviewer_notes && <p className="text-sm text-red-600 mt-0.5"><strong>Reason:</strong> {uploaded.reviewer_notes}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {(status === 'not_uploaded' || status === 'rejected') && (
                              <button onClick={() => handleOpenUploadModal(item.name)} className="flex items-center gap-1.5 px-3 py-2 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-all">
                                <Upload size={14} />{status === 'rejected' ? 'Re-upload' : 'Upload'}
                              </button>
                            )}
                            {uploaded && <button onClick={() => setViewingDocument(uploaded)} className="p-2 border border-slate-200 rounded-lg text-slate-500 hover:text-teal-600 transition-all"><Eye size={16} /></button>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && myCase && (
          <div data-testid="timeline-tab">
            <CaseTimeline caseId={myCase.id} />
          </div>
        )}
      </div>

      {/* ── Officer Document Review Modal ── */}
      {reviewingOfficerDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-5 text-white">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-bold text-lg">Review Document</p>
                  <p className="text-purple-200 text-sm mt-0.5">{reviewingOfficerDoc.item_name}{reviewingOfficerDoc.side_label ? ` — ${reviewingOfficerDoc.side_label}` : ''}</p>
                  <p className="text-purple-200 text-xs mt-0.5">Prepared by your case officer</p>
                </div>
                <button onClick={() => setReviewingOfficerDoc(null)} className="p-1.5 hover:bg-white/20 rounded-lg"><X size={20} /></button>
              </div>
            </div>
            <div className="p-6 space-y-5">
              {reviewingOfficerDoc.file_url && (
                <div className="flex gap-3">
                  <button onClick={() => setViewingDocument({ file_url: reviewingOfficerDoc.file_url, file_name: reviewingOfficerDoc.file_name })}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200">
                    <Eye size={18} /> Open Document
                  </button>
                  <button onClick={() => fileUtils.downloadFile(reviewingOfficerDoc.file_url, reviewingOfficerDoc.file_name)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200">
                    <Download size={18} /> Download
                  </button>
                </div>
              )}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                Please review the document carefully before approving. If anything needs to be corrected, select "Request Changes" and describe what needs to be updated.
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Your feedback <span className="text-slate-400 font-normal">(required only if requesting changes)</span></label>
                <textarea rows={3} value={officerDocNotes} onChange={e => setOfficerDocNotes(e.target.value)}
                  placeholder="Describe what needs to be corrected or updated…"
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none resize-none" />
              </div>
              <div className="flex gap-3">
                <button onClick={() => handleOfficerDocReview('rejected')} disabled={submittingReview}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 disabled:opacity-50">
                  {submittingReview ? <Loader2 size={18} className="animate-spin" /> : <XCircle size={18} />} Request Changes
                </button>
                <button onClick={() => handleOfficerDocReview('approved')} disabled={submittingReview}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-xl font-bold hover:bg-green-600 disabled:opacity-50">
                  {submittingReview ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />} Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Complete File Review Modal ── */}
      {showCompleteFile && completeFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-700 to-purple-700 px-6 py-5 text-white shrink-0">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Shield size={28} className="text-white/80" />
                  <div>
                    <p className="font-bold text-xl">Complete File Review</p>
                    <p className="text-indigo-200 text-sm">Case {completeFile.ucin} — {completeFile.client_name}</p>
                  </div>
                </div>
                <button onClick={() => setShowCompleteFile(false)} className="p-1.5 hover:bg-white/20 rounded-lg"><X size={20} /></button>
              </div>
            </div>

            {/* Document sections */}
            <div className="overflow-y-auto flex-1 p-6 space-y-5">
              {/* Already approved */}
              {completeFile.status === 'approved' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <CheckCircle size={20} className="text-green-600" />
                    <p className="font-bold text-green-800">You Approved This File</p>
                  </div>
                  <div className="text-xs text-green-700 space-y-1 pl-8">
                    <p>Date &amp; Time: {new Date(completeFile.approval?.approved_at).toLocaleString('en-IN')}</p>
                    <p>IP Address: {completeFile.approval?.client_ip_address}</p>
                    <p>Total Documents: {completeFile.approval?.total_documents}</p>
                  </div>
                </div>
              )}

              {/* Section 1 — Client Documents */}
              {completeFile.client_documents?.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Section 1 — Your Documents (uploaded by you)</p>
                  <div className="space-y-1.5">
                    {completeFile.client_documents.map((d, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200">
                        <div className="flex items-center gap-3">
                          <CheckCircle size={16} className="text-green-500 shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-slate-800">{d.checklist_item_name}{d.document_side_label ? ` — ${d.document_side_label}` : ''}</p>
                            {d.applicant_name && <p className="text-xs text-slate-500">For: {d.applicant_name}</p>}
                          </div>
                        </div>
                        {d.file_url && (
                          <div className="flex gap-1">
                            <button onClick={() => setViewingDocument({ file_url: d.file_url, file_name: d.file_name })} className="p-1.5 text-slate-400 hover:text-teal-600"><Eye size={15} /></button>
                            <button onClick={() => fileUtils.downloadFile(d.file_url, d.file_name)} className="p-1.5 text-slate-400 hover:text-teal-600"><Download size={15} /></button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Section 2 — Officer Documents */}
              {completeFile.officer_documents?.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Section 2 — Prepared Documents (by your officer)</p>
                  <div className="space-y-1.5">
                    {completeFile.officer_documents.map((d, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-indigo-50 rounded-xl border border-indigo-200">
                        <div className="flex items-center gap-3">
                          <CheckCircle size={16} className="text-indigo-500 shrink-0" />
                          <p className="text-sm font-semibold text-slate-800">{d.checklist_item_name}{d.document_side_label ? ` — ${d.document_side_label}` : ''}</p>
                        </div>
                        {d.file_url && (
                          <div className="flex gap-1">
                            <button onClick={() => setViewingDocument({ file_url: d.file_url, file_name: d.file_name })} className="p-1.5 text-slate-400 hover:text-teal-600"><Eye size={15} /></button>
                            <button onClick={() => fileUtils.downloadFile(d.file_url, d.file_name)} className="p-1.5 text-slate-400 hover:text-teal-600"><Download size={15} /></button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Section 3 — Government Forms */}
              {completeFile.government_forms?.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Section 3 — Government &amp; Filing Forms</p>
                  <div className="space-y-1.5">
                    {completeFile.government_forms.map((f, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-amber-50 rounded-xl border border-amber-200">
                        <div className="flex items-center gap-3">
                          <FileText size={16} className="text-amber-600 shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-slate-800">{f.name}</p>
                            {f.description && <p className="text-xs text-slate-500">{f.description}</p>}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => setViewingDocument({ file_url: f.file_url, file_name: f.file_name })} className="p-1.5 text-slate-400 hover:text-teal-600"><Eye size={15} /></button>
                          <button onClick={() => fileUtils.downloadFile(f.file_url, f.file_name)} className="p-1.5 text-slate-400 hover:text-teal-600"><Download size={15} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Acknowledgement + Approve (only when pending) */}
              {completeFile.status === 'pending_client' && (
                <div className="space-y-4 border-t border-slate-200 pt-5">
                  <div>
                    <p className="font-bold text-slate-900 mb-1">Formal Acknowledgement</p>
                    <p className="text-xs text-slate-500 mb-4">Please read and confirm each statement below. Your approval is legally binding and will be recorded with a timestamp and your IP address.</p>
                    <div className="space-y-3">
                      {[
                        { key: 'c1', text: 'I have reviewed all documents in my application file and confirm they are complete and accurate.' },
                        { key: 'c2', text: 'I confirm that all information provided is true and accurate to the best of my knowledge.' },
                        { key: 'c3', text: 'I authorise ImmiWays Migration Advisory to submit my visa application on my behalf.' },
                      ].map(({ key, text }) => (
                        <label key={key} className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${cfChecks[key] ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-slate-50 hover:border-indigo-300'}`}>
                          <input type="checkbox" checked={cfChecks[key]} onChange={e => setCfChecks(p => ({ ...p, [key]: e.target.checked }))} className="mt-0.5 w-4 h-4 text-indigo-600 rounded shrink-0" />
                          <span className="text-sm text-slate-700 leading-relaxed">{text}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Request Changes Instead <span className="text-slate-400 font-normal">(describe what needs correction)</span></label>
                      <textarea rows={2} value={cfFeedback} onChange={e => setCfFeedback(e.target.value)}
                        placeholder="e.g. The SOP has an incorrect job title in paragraph 3…"
                        className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-red-400 outline-none resize-none" />
                    </div>
                    <button onClick={handleCfReject} disabled={submittingCf || cfFeedback.trim().length < 20}
                      className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-red-500 text-red-600 rounded-xl font-semibold hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed text-sm">
                      {submittingCf ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />} Request Changes
                    </button>
                    <button onClick={handleCfApprove} disabled={submittingCf || !cfChecks.c1 || !cfChecks.c2 || !cfChecks.c3}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-sm">
                      {submittingCf ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} />} I Approve — Proceed to Filing
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 text-center">Your IP address, browser, and exact timestamp will be recorded as part of this approval.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* File Upload Modal */}
      {showUploadModal && myCase && uploadContext && (
        <FileUpload
          caseId={myCase.id}
          checklistItemName={uploadContext.checklistItemName}
          applicantType={uploadContext.applicantType || 'primary'}
          applicantId={uploadContext.applicantId || null}
          applicantName={uploadContext.applicantName || null}
          documentSideKey={uploadContext.documentSideKey || null}
          documentSideLabel={uploadContext.documentSideLabel || null}
          customRequestId={uploadContext.customRequestId || null}
          onUploadSuccess={handleUploadSuccess}
          onCancel={handleCloseUploadModal}
        />
      )}

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <DocumentViewer
          fileUrl={viewingDocument.file_url}
          fileName={viewingDocument.file_name}
          onClose={() => setViewingDocument(null)}
        />
      )}

      {/* Escalation Modal */}
      {showEscalation && myCase && (
        <EscalationModal
          onClose={() => setShowEscalation(false)}
          onSuccess={() => setShowEscalation(false)}
          caseId={myCase.id}
          clientId={client?.id}
          clientName={client?.full_name}
          ucin={myCase.ucin}
          visaType={client?.visa_type}
          documents={checklist.map(i => i.name)}
          completeFileAvailable={completeFile?.status === 'approved'}
        />
      )}
    </div>
  );
}
